import React, { useEffect, useState } from 'react'
import ViewportMeta from '../../../utils/ViewportMeta'
import { useTranslation } from 'react-i18next'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import newRequest from '../../../utils/newRequest'

const AssetsDashboardCards = () => {
  const { t, i18n } = useTranslation()
  const [categoryData, setCategoryData] = useState([])
  const [locationData, setLocationData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch data from the API
  const fetchChartsData = async () => {
    try {
      const response = await newRequest.get('/assets/getDynamicAssetAnalytics?column=MajorCategoryDescription')
      const data = response.data;
      const categories = data.map(item => item.category)
      const counts = data.map(item => item.count)
      setCategoryData({ categories, counts })
    } catch (error) {
      console.log('Error fetching data:', error);
      setError(`Error: ${error.response?.data?.error || error.message}`)
    }
  }

  // Fetch function for table data
  const fetchLocationData = async () => {
    try {
      const response = await newRequest.get('/assets/getDynamicAssetAnalytics?column=LocationTag')
      const data = response.data;
      setLocationData(data)
    } catch (error) {
      console.log('Error fetching location data:', error);
      setError(`Error: ${error.response?.data?.error || error.message}`)
    }
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchChartsData(), fetchLocationData()])
      .finally(() => {
        setLoading(false)
      })
  }, [])



  // Shimmer placeholder component
  const Shimmer = () => (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-200 rounded mb-4"></div>
      <div className="h-12 bg-gray-200 rounded mb-4"></div>
      <div className="h-12 bg-gray-200 rounded mb-4"></div>
    </div>
  )

  // Error Banner Component
  const ErrorBanner = ({ errorMessage }) => (
    <div className="bg-red-600 text-white p-4 rounded mb-4">
      <p>{errorMessage}</p>
    </div>
  )

  if (loading) {
    return (
      <div className={`p-0 h-full ${i18n.language === 'ar' ? 'sm:mr-72' : 'sm:ml-72'}`}>
        <ViewportMeta />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          <div className="bg-[#17625AC2] text-white p-4 rounded shadow-lg">
            <Shimmer />
          </div>
          <div className="bg-[#48E2D2C2] text-white p-4 rounded shadow-lg">
            <Shimmer />
          </div>
          <div className="bg-[#1EC862C2] text-white p-4 rounded shadow-lg">
            <Shimmer />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 p-4 gap-4 bg-gradient-to-r from-[#95DCEA]">
          <div className="bg-gray-50 w-full shadow-lg rounded p-4">
            <Shimmer />
          </div>
          <div className="bg-gray-50 w-full shadow-lg rounded p-4">
            <Shimmer />
          </div>
          <div className="bg-gray-50 w-full shadow-lg rounded p-4">
            <Shimmer />
          </div>
          <div className="bg-gray-50 w-full shadow-lg rounded p-4 h-[500px]">
            <Shimmer />
          </div>
        </div>
      </div>
    )
  }

  // Chart configurations
  // const barChartOptions = {
  //   chart: {
  //     type: 'column'
  //   },
  //   title: {
  //     text: t('Assets Category')
  //   },
  //   xAxis: {
  //     categories: ['Furniture & Fixtures', 'IT Equipment', 'Vehicles', 'Electrical Equipment', 'Land', 'Tools and Equipments'],
  //   },
  //   yAxis: {
  //     min: 0,
  //     title: {
  //       text: t('Percentage')
  //     },
  //   },
  //   series: [
  //     { name: '2024-01', data: [40, 30, 20, 10, 5, 15] },
  //     { name: '2024-02', data: [20, 50, 30, 20, 15, 10] },
  //     { name: '2024-03', data: [50, 40, 20, 30, 10, 20] },
  //     { name: '2024-04', data: [30, 20, 40, 50, 30, 10] }
  //   ]
  // }

  const barChartOptions = {
    chart: {
      type: 'column'
    },
    title: {
      text: t('Assets Category')
    },
    xAxis: {
      categories: categoryData.categories || [],
    },
    yAxis: {
      min: 0,
      title: {
        text: t('Count')
      },
    },
    series: [
      {
        name: 'Assets',
        data: categoryData.counts || []
      }
    ]
  }

  // const pieChartOptions = {
  //   chart: {
  //     type: 'pie'
  //   },
  //   title: {
  //     text: t('Assets Category - Pie')
  //   },
  //   series: [{
  //     name: 'Assets',
  //     colorByPoint: true,
  //     data: [
  //       { name: 'Furniture & Fixtures', y: 40 },
  //       { name: 'IT Equipment', y: 20 },
  //       { name: 'Vehicles', y: 30 },
  //       { name: 'Electrical Equipment', y: 10 }
  //     ]
  //   }]
  // }

  const pieChartOptions = {
    chart: {
      type: 'pie'
    },
    title: {
      text: 'Assets Category - Pie'
    },
    series: [{
      name: 'Assets',
      colorByPoint: true,
      data: categoryData.categories.map((category, index) => ({
        name: category,
        y: categoryData.counts[index]
      }))
    }]
  }

  const horizontalBarOptions = {
    chart: {
      type: 'bar'
    },
    title: {
      text: t('Assets Request Form')
    },
    xAxis: {
      categories: ['Furniture', 'IT Equipment', 'Vehicles'],
    },
    yAxis: {
      title: {
        text: t('Quantity')
      },
    },
    series: [
      { name: 'Action Type', data: [50, 60, 40] },
      { name: 'Asset ID', data: [30, 40, 50] },
      { name: 'Asset Description', data: [20, 30, 20] },
      { name: 'Request Date', data: [10, 20, 30] },
      { name: 'Status', data: [40, 50, 60] }
    ]
  }

  return (
    <div className={`p-0 h-full ${i18n.language === 'ar' ? 'sm:mr-72' : 'sm:ml-72'}`}>
      <ViewportMeta />

      {/* Error banner */}
      {error && <ErrorBanner errorMessage={error} />}

      {/* Top cards section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
        <div className="flex justify-center items-center gap-2 bg-[#17625AC2] text-white p-4 rounded shadow-lg">
          <h3>{t('New Assets')}</h3>
          <p className="text-2xl">47</p>
        </div>
        <div className="flex justify-center items-center gap-2 bg-[#48E2D2C2] text-white p-4 rounded shadow-lg">
          <h3>{t('Printing Assets')}</h3>
          <p className="text-2xl">47</p>
        </div>
        <div className="bg-[#1EC862C2] flex justify-center items-center gap-2 text-white p-4 rounded shadow-lg">
          <h3>{t('Verified Assets')}</h3>
          <p className="text-2xl">12180</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 p-4 gap-4 bg-gradient-to-r from-[#95DCEA]">
        
        {/* Assets Category - Bar Chart */}
        <div className="relative flex flex-col min-w-0 mb-4 md:mb-0 break-words bg-gray-50 w-full shadow-lg rounded p-4">
          <h2 className="text-xl font-semibold">{t('Assets Category')}</h2>
          <HighchartsReact highcharts={Highcharts} options={barChartOptions} />
        </div>

        {/* Assets Request Form - Horizontal Bar Chart */}
        <div className="relative flex flex-col min-w-0 mb-4 md:mb-0 break-words bg-gray-50 w-full shadow-lg rounded p-4">
          <h2 className="text-xl font-semibold">{t('Assets Request Form')}</h2>
          <HighchartsReact highcharts={Highcharts} options={horizontalBarOptions} />
        </div>

        {/* Assets Category - Pie Chart */}
        <div className="relative flex flex-col min-w-0 break-words bg-gray-50 w-full shadow-lg rounded p-4">
          <h2 className="text-xl font-semibold">{t('Assets Category - Pie')}</h2>
          <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />
        </div>

        {/* Assets Locations - Table */}
        <div className="relative flex flex-col min-w-0 break-words bg-gray-50 w-full shadow-lg rounded p-4 h-[500px]">
          <h2 className="text-xl font-semibold mb-2">{t('Assets Locations')}</h2>
          <div className="overflow-y-auto h-full">
            <table className="min-w-full bg-white border border-gray-300">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b border-gray-300 bg-[#ffff00] text-left font-bold text-black">Location</th>
                  <th className="py-2 px-4 border-b border-gray-300 bg-[#8db4e2] text-left font-bold text-black">Total Assets</th>
                </tr>
              </thead>
              <tbody className='text-center font-bold'>
                {locationData.map((location, index) => (
                  <tr key={index}>
                    <td className="py-2 px-4 border-b border-gray-300">{location.category}</td>
                    <td className="py-2 px-4 border-b border-gray-300">{location.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
      </div>
    </div>
  )
}

export default AssetsDashboardCards